// src/components/PrintSuperTicket.jsx
//
// The pre-printed continuous "superticket" form — 21.5 x 35.5cm (US Legal,
// landscape), fed from the printer's second tray. Six panels, separated by
// scored/perforated lines on the physical stock:
//
//   ┌───────────────┬───────────┬───────────────┐
//   │ recipient /    │ shop info │ gift card      │  row 1
//   │ delivery label │           │ message insert │
//   ├───────────────┴───────────┼───────────────┤
//   │ full working ticket        │ office copy    │  row 2
//   └────────────────────────────┴───────────────┘
//
// This is separate from PrintTicket.jsx on purpose — that component (and
// print.js's existing 'ticket'/'invoice' modes) stays untouched, so the
// working landscape ticket the shop already relies on can't regress.
//
// The panel proportions (--st-col*/--st-row* in index.css) are a first guess
// read off a photo of the shop's stock, not a calibrated value. Print one
// test page on the superticket tray, hold it against a blank sheet, and
// adjust those CSS variables until text lands inside each preprinted box.

import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { schedule, workflowOf, neededDateOf, neededTimeOf, fmtDay } from '../lib/priority';
import { paymentSummary, cancellationOf } from '../lib/invoice';
import { fmt, TYPE_TITLE, fmtNeededDate, fmtTaken, STATUS_WORD } from '../lib/ticketFormat';

export function PrintSuperTicket({ order }) {
  const { shop } = useAuth();
  if (!order) return null;

  const neededDate = neededDateOf(order) || '';
  const neededTime = neededTimeOf(order);
  const customerName = order.customerName
    || `${order.billing?.firstName || ''} ${order.billing?.lastName || ''}`.trim();
  const recipientName = `${order.recipient?.firstName || ''} ${order.recipient?.lastName || ''}`.trim();

  const sched  = schedule(order, workflowOf(shop));
  const pay    = paymentSummary(order);
  const cancel = cancellationOf(order);
  const title  = TYPE_TITLE[order.type] || 'Order';
  const dateLabel = order.type === 'pick_up' ? 'Pick Up Date'
                  : order.type === 'carry_out' ? 'Needed Date'
                  : 'Delivery Date';

  const b = order.billing || {};
  const r = order.recipient || {};

  return createPortal(
    <div className="tk-print-root">
    <div id="print-superticket">

      {/* ── top-left: recipient / delivery label ── */}
      <div className="st-panel st-label">
        {recipientName && <div className="st-strong st-line">{recipientName}</div>}
        {r.address && <div className="st-line">{r.address}</div>}
        {(r.city || r.stPr) && (
          <div className="st-line">{[r.city, r.stPr].filter(Boolean).join(', ')} {r.zipPostal || ''}</div>
        )}
        {r.phone && <div className="st-line">{r.phone}</div>}
        <div className="st-line" style={{ marginTop: '4pt' }}>
          <span className="st-strong">{dateLabel}: </span>{fmtNeededDate(neededDate, neededTime)}
        </div>
        <div className="st-line"><span className="st-strong">Order #</span> {order.orderNumber}</div>
      </div>

      {/* ── top-middle: reserved for the pre-printed letterhead.
          The shop's name/address/phone are already on the stock here —
          printing them again duplicated the letterhead, so this panel is
          left blank until there's something else worth putting in it. */}
      <div className="st-panel st-shopinfo" />

      {/* ── top-right: gift card message insert ── */}
      <div className="st-panel st-cardmsg">
        <div className="st-cardmsg-text">{r.cardMessage || ''}</div>
      </div>

      {/* ── bottom, spanning left+middle: full working ticket ── */}
      <div className="st-panel st-fullticket">
        <div className="st-ft-head">
          <div className="st-ft-title">{title}</div>
          <div>
            <div><span className="st-strong">Order #</span> {order.orderNumber}</div>
            <div><span className="st-strong">{dateLabel}</span> {fmtNeededDate(neededDate, neededTime)}</div>
          </div>
        </div>

        {cancel.cancelled && (
          <div className="st-ft-void">*** CANCELLED — DO NOT MAKE THIS ORDER ***</div>
        )}
        {!cancel.cancelled && (
          <div className={`st-ft-paidband ${pay.settled ? 'is-paid' : 'is-due'}`}>
            {pay.settled ? <>PAID{pay.method ? ` — ${pay.method}` : ''}</> : <>NOT PAID — COLLECT {fmt(pay.amountDue || order.total)}</>}
          </div>
        )}

        <div className="st-ft-grid">
          <div className="st-ft-box">
            <div className="st-sub">Customer</div>
            <div className="st-strong">{customerName || '—'}</div>
            {b.phone && <div>{b.phone}</div>}
          </div>
          <div className="st-ft-box">
            <div className="st-sub">Sold By / Taken</div>
            <div>{order.receivedBy || '—'}</div>
            <div>{fmtTaken(order.createdAt)}</div>
          </div>
          <div className="st-ft-box">
            <div className="st-sub">Occasion / Status</div>
            <div>{r.occasion || 'Other'}</div>
            <div>{STATUS_WORD[order.status] || order.status || '—'}</div>
          </div>
        </div>

        <table className="st-items">
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Price</th><th>Ext.</th></tr>
          </thead>
          <tbody>
            {(order.items || []).filter(i => i.description).map((item, i) => {
              const qty = Number(item.qty) || 0;
              const price = Number(item.price) || 0;
              const disc = Number(item.discount) || 0;
              return (
                <tr key={i}>
                  <td>{item.description}</td>
                  <td>{qty}</td>
                  <td>{fmt(price)}</td>
                  <td>{fmt(qty * price * (1 - disc / 100))}</td>
                </tr>
              );
            })}
            {!(order.items || []).some(i => i.description) && (
              <tr><td colSpan={4} className="st-sub">No items on this order.</td></tr>
            )}
          </tbody>
        </table>

        <div className="st-ft-money">
          <span><span className="st-strong">Total</span> {fmt(order.total)}</span>
          <span>{order.delivery?.instructions || ''}</span>
        </div>
      </div>

      {/* ── bottom-right: compact office copy ── */}
      <div className="st-panel st-officecopy">
        <div className="st-strong st-line">Order # {order.orderNumber}</div>
        <div className="st-line">Store: {shop?.name}</div>
        <div className="st-line">{dateLabel}: {fmtNeededDate(neededDate, neededTime)}</div>
        {recipientName && <div className="st-line" style={{ marginTop: '3pt' }}>{recipientName}</div>}
        {r.address && <div className="st-line">{r.address}</div>}
        {(r.city || r.stPr) && <div className="st-line">{[r.city, r.stPr].filter(Boolean).join(', ')} {r.zipPostal || ''}</div>}
        <div className="st-line" style={{ marginTop: '3pt' }}>Sold To: {customerName || '—'}</div>
        {b.phone && <div className="st-line">{b.phone}</div>}
        <div className="st-line">Occ: {r.occasion || 'Other'}</div>
        {order.delivery?.zone && <div className="st-line">Del. Area: {order.delivery.zone}</div>}
        {b.auth && <div className="st-line st-sub">CC Auth: {b.auth}</div>}
      </div>

    </div>
    </div>,
    document.body
  );
}
